import { BottomSheetFlatList } from "@-/com.npmjs.gorhom__bottom-sheet";
import {
  ActivityIndicator,
  Keyboard,
  Pressable,
  ScrollView,
  View,
} from "@-/com.npmjs.react-native";
import { withUniwind } from "@-/com.npmjs.uniwind";
import {
  BottomSheet,
  type BottomSheetHandle,
  BottomSheetTextInput,
} from "@-/dev.improve.haniverse.components.ui.bottom-sheet";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useDebouncedMorphemesSearch } from "@-/dev.improve.haniverse.lib.morpheme.search";
import {
  type Context,
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { MorphemeSearchSheetResultRow } from "#morpheme-search-sheet-result-row";

const UniwindBottomSheetTextInput = withUniwind(BottomSheetTextInput);

/** React Native's `ScrollView.Context`: exported at runtime, omitted from its bundled typings. */
const ScrollViewContext =
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the static exists at runtime (ScrollView.js); only the typings omit it
  (ScrollView as typeof ScrollView & { Context: Context<{ horizontal: boolean } | null> }).Context;

export type MorphemeSearchSheetHandle = BottomSheetHandle;

export type MorphemeSearchSheetControls = {
  /** Return from the selection step to the results, keeping the query. */
  back: () => void;
  close: () => void;
};

type MorphemeSearchSheetSelectionProps =
  | {
      /** Called with the picked result. The sheet stays open until `close` is called. */
      onSelect: (
        morpheme: MorphemeDetailsTileSlice,
        controls: MorphemeSearchSheetControls,
      ) => void;
      renderSelection?: never;
    }
  | {
      onSelect?: never;
      /**
       * A second step rendered inside the sheet once a result is picked, under a Back control:
       * e.g. choosing which of the morpheme's cards to use.
       */
      renderSelection: (
        morpheme: MorphemeDetailsTileSlice,
        controls: MorphemeSearchSheetControls,
      ) => ReactNode;
    };

export type MorphemeSearchSheetProps = MorphemeSearchSheetSelectionProps & {
  /** Which results to list; `all` mixes characters and words. */
  category?: "all" | "characters" | "words";
  description?: string;
  /** Fires after the dismissal animation completes. */
  onDismiss?: () => void;
  placeholder?: string;
  ref?: Ref<MorphemeSearchSheetHandle>;
  testID?: string;
  title: string;
};

/**
 * The app's character/word search as a picker: a full-height bottom sheet with the Search tab's
 * query (`useDebouncedMorphemesSearch`) whose results are chosen rather than navigated to. Drive it
 * through the `{ open, close }` handle on `ref`. Each opening starts from an empty query.
 *
 * Needs a `BottomSheetModalProvider` ancestor in the window it should appear in: the app root's,
 * or a nearer one inside an overlay that sits above the root (see the Admin panel).
 */
export function MorphemeSearchSheet({
  onDismiss,
  ref,
  testID = "morpheme-search-sheet",
  ...props
}: MorphemeSearchSheetProps) {
  const sheetRef = useRef<BottomSheetHandle>(null);
  useImperativeHandle(
    ref,
    () => ({
      close: () => {
        sheetRef.current?.close();
      },
      open: () => {
        sheetRef.current?.open();
      },
    }),
    [],
  );
  return (
    <BottomSheet fullHeight hasScrollableContent onDismiss={onDismiss} ref={sheetRef}>
      {/* The sheet's portal bridge carries every context from where the sheet is declared, so one
        declared inside a scroll view (the Admin panel's) would make its results list read as a
        list nested in that scroll view. It is its own surface: reset the scroll context. */}
      <ScrollViewContext value={null}>
        <MorphemeSearchSheetContent
          {...props}
          close={() => {
            Keyboard.dismiss();
            sheetRef.current?.close();
          }}
          testID={testID}
        />
      </ScrollViewContext>
    </BottomSheet>
  );
}

function MorphemeSearchSheetContent({
  category = "all",
  close,
  description,
  onSelect,
  placeholder = "Type a character, pinyin, meaning",
  renderSelection,
  testID,
  title,
}: Omit<MorphemeSearchSheetProps, "onDismiss" | "ref" | "testID"> & {
  close: () => void;
  testID: string;
}) {
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState<MorphemeDetailsTileSlice | null>(null);
  const controls = useMemo<MorphemeSearchSheetControls>(
    () => ({ back: () => setSelection(null), close }),
    [close],
  );

  if (selection !== null && renderSelection !== undefined) {
    return (
      <View className="flex-1 gap-3" testID={`${testID}-selection`}>
        <Pressable
          accessibilityLabel="Back to results"
          accessibilityRole="button"
          className="-ml-1 flex-row items-center self-start"
          hitSlop={8}
          onPress={controls.back}
          testID={`${testID}-back`}
        >
          <Icon className="text-muted-foreground" name="FiChevronLeft" size={18} />
          <Text className="text-sm text-muted-foreground">Results</Text>
        </Pressable>
        {renderSelection(selection, controls)}
      </View>
    );
  }

  return (
    <View className="flex-1" testID={testID}>
      <View className="gap-1 pb-3">
        <Text accessibilityRole="header" className="text-lg font-semibold text-foreground">
          {title}
        </Text>
        {description === undefined ? null : (
          <Text className="text-sm text-muted-foreground">{description}</Text>
        )}
      </View>
      <UniwindBottomSheetTextInput
        accessibilityLabel="Search"
        autoCapitalize="none"
        autoCorrect={false}
        autoFocus
        className="h-11 rounded-xs border border-input bg-background px-3 font-sans text-base text-foreground dark:bg-input/30"
        clearButtonMode="while-editing"
        onChangeText={setQuery}
        placeholder={placeholder}
        returnKeyType="search"
        testID={`${testID}-input`}
        value={query}
      />
      <MorphemeSearchSheetResults
        category={category}
        onPick={(morpheme) => {
          Keyboard.dismiss();
          if (renderSelection === undefined) {
            // The props union requires `onSelect` whenever `renderSelection` is absent; destructuring
            // loses that correlation.
            onSelect?.(morpheme, controls);
          } else {
            setSelection(morpheme);
          }
        }}
        query={query}
        testID={testID}
      />
    </View>
  );
}

function MorphemeSearchSheetResults({
  category,
  onPick,
  query,
  testID,
}: {
  category: "all" | "characters" | "words";
  onPick: (morpheme: MorphemeDetailsTileSlice) => void;
  query: string;
  testID: string;
}) {
  const search = useDebouncedMorphemesSearch(query, { category });
  const results = useMemo(
    () => search.data?.pages.flatMap((page) => page.cards) ?? [],
    [search.data],
  );
  const hasQuery = search.debouncedQuery.trim().length > 0;

  const emptyState = (() => {
    if (!hasQuery) {
      return <Hint text="Search by character, pinyin or meaning." />;
    }
    if (search.isPending || search.debouncedQuery !== query) {
      return (
        <View accessibilityLabel="Searching" className="h-32 items-center justify-center">
          <ActivityIndicator />
        </View>
      );
    }
    if (search.isError) {
      return (
        <Pressable
          accessibilityRole="button"
          className="py-6"
          onPress={() => void search.refetch()}
        >
          <Hint text="Search failed. Tap to retry." />
        </Pressable>
      );
    }
    return <Hint text={`No matches for “${search.debouncedQuery.trim()}”.`} />;
  })();

  return (
    <BottomSheetFlatList
      className="mt-3 flex-1"
      data={hasQuery ? results : []}
      keyboardDismissMode="on-drag"
      keyboardShouldPersistTaps="handled"
      keyExtractor={(morpheme) => `${morpheme.morphemeType}:${morpheme.front}`}
      ListEmptyComponent={emptyState}
      ListFooterComponent={
        search.isFetchingNextPage ? (
          <View className="py-4">
            <ActivityIndicator />
          </View>
        ) : null
      }
      onEndReached={() => {
        if (search.hasNextPage && !search.isFetchingNextPage) {
          void search.fetchNextPage();
        }
      }}
      onEndReachedThreshold={0.5}
      renderItem={({ item }) => <MorphemeSearchSheetResultRow morpheme={item} onPress={onPick} />}
      testID={`${testID}-results`}
    />
  );
}

function Hint({ text }: { text: string }) {
  return <Text className="py-6 text-center text-sm text-muted-foreground">{text}</Text>;
}
